import { useEffect, useState } from "react";
import { Loader2, Star } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Reveal } from "@/components/motion";

const inputClass =
  "h-11 w-full rounded-md border border-clay bg-white px-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none transition-colors duration-300";

export const Stars = ({ value, size = "h-4 w-4" }) => (
  <div className="flex gap-0.5" aria-label={`${value} sur 5 étoiles`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= value ? "fill-ochre text-ochre" : "text-clay"}`} />
    ))}
  </div>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

const ProductReviews = ({ productId }) => {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [productId]);

  const average = reviews.length ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : null;

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Choisissez une note en étoiles");
      return;
    }
    setSending(true);
    try {
      await api.post(`/products/${productId}/reviews`, { author_name: name, rating, comment });
      setSent(true);
      toast.success("Merci ! Votre avis sera publié après vérification.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <section data-testid="product-reviews" className="mt-28">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-terracotta">Avis clients</p>
            <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-bark sm:text-4xl">
              Ce qu'en pensent nos clients
            </h2>
          </div>
          {average && (
            <div className="flex items-center gap-3" data-testid="reviews-average">
              <Stars value={Math.round(average)} size="h-5 w-5" />
              <span className="font-display text-2xl font-semibold text-bark">{average}</span>
              <span className="text-sm text-bark/50">({reviews.length} avis)</span>
            </div>
          )}
        </div>
      </Reveal>

      <div className="mt-12 grid gap-12 lg:grid-cols-12">
        {/* Liste des avis */}
        <div className="lg:col-span-7">
          {reviews.length === 0 ? (
            <p data-testid="reviews-empty" className="border border-clay bg-white p-8 text-sm text-bark/50">
              Aucun avis pour le moment — soyez la première personne à partager votre expérience.
            </p>
          ) : (
            <ul className="space-y-4" data-testid="reviews-list">
              {reviews.map((r) => (
                <li key={r.id} data-testid={`review-${r.id}`} className="border border-clay bg-white p-6">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-clay/60 font-display text-lg font-semibold text-terracotta">
                        {r.author_name.charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-bark" data-testid={`review-author-${r.id}`}>{r.author_name}</p>
                        <p className="text-xs text-bark/45">{formatDate(r.created_at)}</p>
                      </div>
                    </div>
                    <Stars value={r.rating} />
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-bark/70">{r.comment}</p>
                  {r.admin_reply && (
                    <div className="mt-4 border-l-2 border-terracotta bg-cream p-4" data-testid={`review-admin-reply-${r.id}`}>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-terracotta">Réponse de Sama-Keur</p>
                      <p className="mt-1 text-sm leading-relaxed text-bark/70">{r.admin_reply}</p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Formulaire */}
        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            {sent ? (
              <div data-testid="review-thanks" className="border border-terracotta/30 bg-cream p-8 text-center">
                <p className="font-display text-xl font-semibold text-bark">Merci pour votre avis !</p>
                <p className="mt-2 text-sm text-bark/60">
                  Il sera publié sur cette page après vérification par notre équipe.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="review-form" className="space-y-4 border border-clay bg-cream p-6">
                <p className="font-display text-xl font-semibold text-bark">Laisser un avis</p>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Votre note *</label>
                  <div className="flex gap-1" data-testid="review-rating-input">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <button
                        key={i}
                        type="button"
                        data-testid={`review-star-${i}`}
                        onClick={() => setRating(i)}
                        onMouseEnter={() => setHover(i)}
                        onMouseLeave={() => setHover(0)}
                        className="p-1 transition-transform duration-150 hover:scale-110"
                        aria-label={`${i} étoile${i > 1 ? "s" : ""}`}
                      >
                        <Star className={`h-8 w-8 transition-colors ${i <= (hover || rating) ? "fill-ochre text-ochre" : "text-clay"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Votre prénom *</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex : Awa" className={inputClass} data-testid="review-name-input" maxLength={80} />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.25em] text-terracotta">Votre commentaire *</label>
                  <textarea
                    required
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Qualité, livraison, conseils…"
                    className="min-h-[100px] w-full rounded-md border border-clay bg-white px-3 py-3 text-sm text-bark placeholder:text-bark/35 focus:border-terracotta focus:outline-none"
                    data-testid="review-comment-input"
                    maxLength={1000}
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="review-submit-button"
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-terracotta text-xs font-bold uppercase tracking-[0.15em] text-sand transition-colors hover:bg-terracotta-dark disabled:opacity-50"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Publier mon avis
                </button>
                <p className="text-center text-[11px] text-bark/40">Votre avis est relu par notre équipe avant publication.</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ProductReviews;
